import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { campaignKeys } from '../../keys/campaign-keys';
import { useCampaignStore } from '../../campaign-store/campaign-store';

export function useCampaigns() {
  return useQuery(campaignKeys.list());
}

export function useCampaign(id: number | null | undefined) {
  return useQuery({
    ...campaignKeys.detail(id ?? 0),
    enabled: !!id,
  });
}

export type UseInitCampaignOptions = {
  /** Always use the campaign with is_current (mobile — no campaign picker). */
  lockToCurrent?: boolean;
};

/** Sync store with API list: default to is_current or first campaign. */
export function useInitCampaign(options?: UseInitCampaignOptions) {
  const lockToCurrent = options?.lockToCurrent ?? false;
  const campaigns = useCampaigns();
  const campaignId = useCampaignStore((s) => s.campaignId);
  const setCampaignId = useCampaignStore((s) => s.setCampaignId);

  useEffect(() => {
    if (!campaigns.data?.length) {
      return;
    }
    const current =
      campaigns.data.find((c) => c.is_current) ?? campaigns.data[0];
    if (!current) {
      return;
    }

    if (lockToCurrent) {
      if (campaignId !== current.id) {
        setCampaignId(current.id);
      }
      return;
    }

    const known = campaigns.data.some((c) => c.id === campaignId);
    if (campaignId && known) {
      return;
    }
    setCampaignId(current.id);
  }, [campaigns.data, campaignId, lockToCurrent, setCampaignId]);

  return campaigns;
}

/** Mobile: always send X-Campaign-Year for the is_current campaign. */
export function useInitCurrentCampaign() {
  return useInitCampaign({ lockToCurrent: true });
}

export default useCampaigns;
